import { Grid, ListItem, ListItemText } from '@material-ui/core';
import { FormikCheckboxField } from '@superdispatch/forms';
import { InfoTooltip, Inline, Stack, useUID } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { requestNotificationPermission } from 'shared/helpers/notifications/BrowserNotification';
import {
  isSavedSearchDisabled,
  NotificationSettingsValuesDTO,
  NotificationsSettingActivity,
} from './data/LoadboardNotificationsSettingDTO';
import { UserSettingsPhoneState } from './data/LoadboardUserSettingsDTO';
import { NotificationSettingPhoneWarning } from './NotificationSettingPhoneWarning';
import { LoadAlertDisabledWarning } from './NotificationSettingWarnings';

const notificationActivities = new Map<
  NotificationsSettingActivity,
  { title: string; subheader: string }
>([
  [
    'saved_search',
    { title: 'Load Alerts', subheader: 'Loads that match created load alerts' },
  ],
  [
    'load_suggested',
    { title: 'Suggested Loads', subheader: 'Loads that match past routes' },
  ],
  [
    'request_declined',
    { title: 'Declined Requests', subheader: 'Requests declined by shipper' },
  ],
]);

interface NotificationSettingActivityProps {
  showWarnings?: boolean;
  isLoading: boolean | undefined;
  name: NotificationsSettingActivity;
  phoneState?: UserSettingsPhoneState;
}

export function NotificationSettingActivity({
  name,
  isLoading,
  showWarnings,
  phoneState = 'valid',
}: NotificationSettingActivityProps) {
  const uid = useUID();
  const { values } = useFormikContext<NotificationSettingsValuesDTO>();
  const activity = notificationActivities.get(name);

  if (!activity) {
    return null;
  }

  return (
    <ListItem
      aria-labelledby={uid}
      disableGutters={true}
      style={{ padding: 0 }}
    >
      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12}>
          <Grid
            spacing={3}
            container={true}
            alignItems="center"
            justify="space-between"
          >
            <Grid item={true} xs={false} sm="auto">
              <Stack space="xsmall">
                <ListItemText
                  primary={activity.title}
                  secondary={activity.subheader}
                  primaryTypographyProps={{ id: uid, variant: 'h4' }}
                />

                {phoneState === 'blacklisted' && (
                  <NotificationSettingPhoneWarning state="blacklisted" />
                )}
              </Stack>
            </Grid>

            <Grid item={true} xs={12} sm="auto">
              <Grid container={true} spacing={2}>
                {name !== 'request_declined' && (
                  <Grid item={true} xs={12} sm="auto">
                    <Grid container={true} spacing={2} alignItems="center">
                      {showWarnings &&
                        (phoneState === 'invalid' ||
                          phoneState === 'missing') && (
                          <Grid item={true} xs={12} sm="auto">
                            <NotificationSettingPhoneWarning
                              state={phoneState}
                            />
                          </Grid>
                        )}

                      <Grid item={true} xs={12} sm="auto">
                        <FormikCheckboxField
                          label="SMS"
                          name={`${name}.sms_enabled`}
                          disabled={isLoading || phoneState !== 'valid'}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid item={true} xs={12} sm="auto">
                  <FormikCheckboxField
                    label="Email"
                    disabled={isLoading}
                    name={`${name}.email_enabled`}
                  />
                </Grid>

                {name !== 'load_suggested' && !MobileAppBridge.isInjected() && (
                  <Grid item={true} xs={12} sm="auto">
                    <FormikCheckboxField
                      label={
                        <Inline space="xxsmall">
                          <TextBox>Web Push</TextBox>
                          <InfoTooltip title="Instant updates delivered to your browser" />
                        </Inline>
                      }
                      disabled={isLoading}
                      name={`${name}.push_enabled`}
                      onChange={(_, checked) => {
                        if (checked) {
                          void requestNotificationPermission();
                        }
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {name === 'saved_search' &&
          isSavedSearchDisabled(values.saved_search, phoneState) && (
            <Grid item={true}>
              <LoadAlertDisabledWarning />
            </Grid>
          )}
      </Grid>
    </ListItem>
  );
}
